import { Cancel, Save } from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import { mutationErrorHandler } from "@services/relay/utils";
import { type FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { graphql, useFragment, useMutation } from "react-relay";

import LocationSelect from "./LocationSelect";
import { type LocationsListItemEditMutation } from "./__generated__/LocationsListItemEditMutation.graphql";
import { type LocationsListItemEdit_location$key } from "./__generated__/LocationsListItemEdit_location.graphql";

type Props = {
  locationRef: LocationsListItemEdit_location$key;
  finishEdit: () => void;
};

export type BrandLocationInputs = {
  id: string;
  place: google.maps.places.AutocompletePrediction | null;
  coords?: [number, number];
};

const locationFragment = graphql`
  fragment LocationsListItemEdit_location on BrandLocationType {
    id
    address
    googlePlaceId
    point {
      coordinates
    }
  }
`;

const mutation = graphql`
  mutation LocationsListItemEditMutation($input: BrandLocationUpsertInput!) {
    brandLocationUpsert(input: $input) {
      brand {
        ...LocationsList_brand
      }
    }
  }
`;

const LocationsListItemEdit: FC<Props> = ({ locationRef, finishEdit }) => {
  const location = useFragment(locationFragment, locationRef);
  const [commitMutation, mutationLoading] = useMutation<LocationsListItemEditMutation>(mutation);

  const methods = useForm<BrandLocationInputs>({
    defaultValues: {
      id: location.id,
      place: null,
    },
  });

  const coords = methods.watch("coords");

  const onSubmit = (data: BrandLocationInputs): void => {
    if (data.place == null) {
      throw new Error("No place selected unexpectedly");
    } else if (data.coords == null) {
      throw new Error("No coordinates selected unexpectedly");
    }

    commitMutation({
      variables: {
        input: {
          id: data.id,
          address: data.place.description,
          googlePlaceId: data.place.place_id,
          point: {
            type: "POINT",
            coordinates: data.coords,
          },
        },
      },
      onCompleted: finishEdit,
      onError: mutationErrorHandler,
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          const wrappedOnSubmit = methods.handleSubmit(onSubmit);
          void wrappedOnSubmit(e);
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <LocationSelect />

          <Stack direction="row" spacing={1}>
            <IconButton onClick={finishEdit}>
              <Cancel />
            </IconButton>
            <IconButton type="submit" disabled={coords == null || mutationLoading}>
              <Save color={coords == null || mutationLoading ? "disabled" : "success"} />
            </IconButton>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default LocationsListItemEdit;
